// The measuring tool (issue 832, BOARD-005): a mode in which a drag from one
// square to another shows the distance by the table's rule, until the next
// drag or Escape. Local to this person's screen (the recommendation on the
// issue): nothing is sent. Held outside React, like the pointing tool.
import type { Cell, Point } from '@dragons-table/vtt-shared';
import { wallTool } from './wallTool';
import { focusTool } from './focusTool';
import { targetTool } from './targetTool';
import { areaTool } from './areaTool';
import { fogTool } from './fogTool';
import { zoneTool } from './zoneTool';
import { drawTool } from './drawTool';
import { whatsThisTool } from './whatsThisTool';

/** `fromAt`/`toAt`: the board points under the hand, which a map with no grid measures between (issue 1035). */
/** `follow`: started from the board's menu (qa-runs#1425), the ruler's end follows the pointer until the next press. */
export interface Ruler { on: boolean; from: Cell | null; to: Cell | null; fromAt?: Point; toAt?: Point; follow?: boolean }

type Listener = (r: Ruler) => void;
let ruler: Ruler = { on: false, from: null, to: null };
const listeners = new Set<Listener>();
const set = (next: Ruler) => { ruler = next; for (const l of listeners) l(ruler); };

export const measureTool = {
  get: () => ruler,
  subscribe(listener: Listener): () => void { listeners.add(listener); return () => { listeners.delete(listener); }; },
  start(): void { whatsThisTool.stop(); focusTool.stop(); areaTool.stop(); targetTool.stop(); fogTool.stop(); drawTool.stop(); wallTool.stop(); zoneTool.stop(); set({ on: true, from: null, to: null }); },
  stop(): void { if (ruler.on || ruler.from) set({ on: false, from: null, to: null }); },
  toggle(): void { if (ruler.on) measureTool.stop(); else measureTool.start(); },
  /** Measure from a spot (qa-runs#1425): the ruler starts there and its end follows the pointer; the next press sets it down. */
  from(c: Cell, at: Point): void { measureTool.start(); set({ on: true, from: c, to: c, fromAt: at, toAt: at, follow: true }); },
  /** Set down a following ruler where it is: it stays until the next drag, or Escape. */
  settle(): void { if (ruler.follow) set({ ...ruler, follow: false }); },
  /** The drag: where it started and where the hand is now. */
  span(from: Cell, to: Cell, fromAt?: Point, toAt?: Point): void { if (ruler.on) set({ on: true, from, to, ...(fromAt && toAt ? { fromAt, toAt } : {}), ...(ruler.follow ? { follow: true } : {}) }); },
};
